// The delivery page's rules, pure (owner, 2026-10-10 — « Watch, don't click »).
//
// Lanes come from the worker (lib/ameexBoard.ts); this file orders them, says
// each problem in one plain line, and places cities on the little map. Every
// word is built from AMEEX's own facts — nothing here guesses.
import type { AmeexBoardParcel, AmeexLane, AmeexParcelDetail, AmeexCheckReason } from '@/lib/api';
import type { TFunc } from '@/lib/i18n';

export const LANES: AmeexLane[] = ['cree', 'route', 'livraison', 'livre'];
export const LANE_LABEL: Record<AmeexLane, string> = {
  cree: 'Créé', route: 'En route', livraison: 'En livraison', livre: 'Livré (7 j)',
};

const ms = (iso: string | null | undefined) => { const t = Date.parse(String(iso ?? '')); return Number.isFinite(t) ? t : 0; };

/** Problems first, the longest-waiting on top; then the most recent movement. Pure. */
export function orderLane(parcels: AmeexBoardParcel[]): AmeexBoardParcel[] {
  return [...parcels].sort((a, b) => {
    if (Boolean(a.problem) !== Boolean(b.problem)) return a.problem ? -1 : 1;
    if (a.problem && b.problem) return ms(a.lastEventAt) - ms(b.lastEventAt);
    return ms(b.lastEventAt) - ms(a.lastEventAt);
  });
}

/**
 * Can this parcel be deleted from the page (owner, 2026-10-31: « some parcels
 * are errors and need to be deleted »)? Only while AMEEX has never picked it
 * up — after that the courier holds a real box and AMEEX refuses anyway. Pure.
 */
export function canDeleteParcel(p: Pick<AmeexBoardParcel, 'lane' | 'shopifyOrderId'>): boolean {
  return p.lane === 'cree' && Boolean(p.shopifyOrderId);
}

/** « 3 h », « 2 j » — how long. Pure. */
export function since(iso: string | null | undefined, nowMs: number): string {
  const t = ms(iso);
  if (!t) return '';
  const min = Math.max(0, Math.floor((nowMs - t) / 60_000));
  if (min < 60) return `${Math.max(1, min)} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} j`;
}

/** The chip's short problem word. */
export function problemShort(p: AmeexBoardParcel, t: TFunc): string | null {
  if (!p.problem) return null;
  switch (p.problem.kind) {
    case 'unreachable': return t('injoignable');
    case 'refused': return t('refusé');
    case 'silent': return t('sans nouvelles');
    case 'not_picked': return t('pas ramassé');
  }
}

const hm = (iso: string, lang: string) => new Date(iso).toLocaleTimeString(lang === 'en' ? 'en-GB' : 'fr-FR', { timeZone: 'Africa/Casablanca', hour: '2-digit', minute: '2-digit' });

/**
 * « Client injoignable 2×. L'agent lui a écrit à 11:05, pas de réponse. »
 * Built from the panel's live facts; null when there is no problem.
 */
export function problemLine(p: AmeexBoardParcel, d: AmeexParcelDetail | null, t: TFunc, lang: string, nowMs: number): string | null {
  if (!p.problem) return null;
  if (p.problem.kind === 'unreachable') {
    const n = Math.max(1, d?.attempts ?? 1);
    const head = t('Le livreur n’a pas pu la joindre {n}×.', { n });
    if (!d?.agentAt) return head;
    return `${head} ${d.repliedAt
      ? t('L’agent lui a écrit sur WhatsApp à {a} — elle a répondu à {b}.', { a: hm(d.agentAt, lang), b: hm(d.repliedAt, lang) })
      : t('L’agent lui a écrit sur WhatsApp à {a} — pas encore de réponse.', { a: hm(d.agentAt, lang) })}`;
  }
  if (p.problem.kind === 'refused') {
    const last = [...(d?.events ?? [])].reverse().find((e) => e.problem);
    return last ? t('Refusé à la porte — {why}.', { why: t(last.name) }) : t('Refusé à la porte.');
  }
  if (p.problem.kind === 'not_picked') return t('AMEEX n’a pas encore ramassé ce colis — créé il y a {w}.', { w: since(p.lastEventAt, nowMs) });
  return t('Aucune nouvelle d’AMEEX depuis {w}.', { w: since(p.lastEventAt, nowMs) });
}

export const CHECK_LABEL: Record<AmeexCheckReason, string> = {
  old_unshipped: 'Plus de 30 jours, jamais expédiée',
  never_picked: 'Colis créé, jamais ramassé par AMEEX',
  silent_long: 'Plus aucune nouvelle d’AMEEX depuis 30 jours',
  returned: 'Retourné — pas encore reçu en stock',
  cancelled_parcel: 'Commande annulée — le colis existe encore chez AMEEX',
};

// ─── THE MAP ────────────────────────────────────────────────────────────────
//
// AMEEX gives a city and a hub per scan — never a position. So the map draws
// Kénitra → the hubs the parcel went through → her city, and nothing more.

/** Approximate centres (lat, lon) of the cities AMEEX serves most. */
export const CITY_COORDS: Record<string, [number, number]> = {
  kenitra: [34.261, -6.58], rabat: [34.02, -6.841], sale: [34.053, -6.798], temara: [33.928, -6.906],
  casablanca: [33.573, -7.589], mohammedia: [33.686, -7.383], 'el jadida': [33.254, -8.506], settat: [33.001, -7.616],
  berrechid: [33.265, -7.587], marrakech: [31.63, -7.981], agadir: [30.428, -9.598], inezgane: [30.355, -9.537],
  'ait melloul': [30.334, -9.497], taroudant: [30.47, -8.877], tiznit: [29.698, -9.731], essaouira: [31.513, -9.77],
  safi: [32.299, -9.237], 'beni mellal': [32.337, -6.349], khouribga: [32.881, -6.906], fes: [34.033, -5.0],
  meknes: [33.895, -5.554], ifrane: [33.533, -5.107], taza: [34.21, -4.01], oujda: [34.681, -1.908],
  nador: [35.168, -2.933], berkane: [34.92, -2.32], 'al hoceima': [35.247, -3.937], tetouan: [35.578, -5.368],
  tanger: [35.759, -5.834], tangier: [35.759, -5.834], fnideq: [35.849, -5.358], mdiq: [35.685, -5.325],
  martil: [35.616, -5.275], larache: [35.193, -6.156], 'ksar el kebir': [35.002, -5.906], chefchaouen: [35.171, -5.27],
  ouazzane: [34.797, -5.584], 'sidi kacem': [34.226, -5.708], 'sidi slimane': [34.265, -5.925], khemisset: [33.824, -6.066],
  laayoune: [27.125, -13.162], dakhla: [23.684, -15.958], guelmim: [28.987, -10.057], 'tan tan': [28.438, -11.103],
  errachidia: [31.931, -4.424], ouarzazate: [30.919, -6.894], midelt: [32.68, -4.735], khenifra: [32.935, -5.669],
  azrou: [33.434, -5.221], guercif: [34.226, -3.354], taourirt: [34.407, -2.893], benslimane: [33.618, -7.12],
  skhirat: [33.852, -7.031], bouznika: [33.789, -7.16], youssoufia: [32.246, -8.529], 'kelaa des sraghna': [32.058, -7.41],
  sefrou: [33.829, -4.828], tinghir: [31.514, -5.533], zagora: [30.332, -5.838], 'ben guerir': [32.236, -7.954],
  'sidi bennour': [32.651, -8.425], azilal: [31.961, -6.571], jerada: [34.311, -2.163], driouch: [34.976, -3.391],
  'souk el arbaa': [34.686, -5.998], 'sidi yahya el gharb': [34.305, -6.306],
};

/** The labelled cities on every map, for orientation. */
export const REFERENCE_CITIES = ['tanger', 'kenitra', 'casablanca', 'fes', 'oujda', 'marrakech', 'agadir'];

/** « Kenitra Hub Principal », « Kénitra », « TANGER » → « kenitra », « tanger ». Pure. */
export function cityKey(name: string | null | undefined): string | null {
  const n = String(name ?? '').toLowerCase().normalize('NFD').replace(/\p{M}+/gu, '')
    .replace(/\bhub\b.*$/, '').replace(/['’-]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!n) return null;
  if (CITY_COORDS[n]) return n;
  const hit = Object.keys(CITY_COORDS).find((k) => n === k || n.startsWith(`${k} `));
  return hit ?? null;
}

/** Origin → each hub the parcel passed through (in order, no repeat) → her city. Pure. */
export function routeOf(d: Pick<AmeexParcelDetail, 'origin' | 'events' | 'city'> | null): string[] {
  if (!d) return [];
  const out: string[] = [];
  const push = (k: string | null) => { if (k && out[out.length - 1] !== k) out.push(k); };
  push(cityKey(d.origin) ?? 'kenitra');
  for (const e of d.events) push(cityKey(e.hub) ?? cityKey(e.city));
  push(cityKey(d.city));
  return out;
}

/** The city of AMEEX's last scan (a hub or a city), or the origin before any scan. Pure. */
export function lastScanCity(d: Pick<AmeexParcelDetail, 'origin' | 'events'> | null): string | null {
  if (!d) return null;
  for (const e of [...d.events].reverse()) {
    const k = cityKey(e.hub) ?? cityKey(e.city);
    if (k) return k;
  }
  return cityKey(d.origin) ?? 'kenitra';
}
